import React, { useEffect, useRef, useState } from 'react';
import { hapticLight } from '../utils/haptics';
import './SearchModal.css';

const RECENTS_KEY = 'poupix_searches';

const SEARCH_CATEGORIES = [
  { label: 'Mercearia', emoji: '🥫' },
  { label: 'Frescos', emoji: '🥦' },
  { label: 'Bebidas', emoji: '🥤' },
  { label: 'Higiene', emoji: '🧼' },
  { label: 'Padaria', emoji: '🍞' },
  { label: 'Bebé', emoji: '🍼' },
];

const getRecents = () => {
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]').slice(0, 8); }
  catch { return []; }
};

const saveRecent = (term) => {
  try {
    const updated = [term, ...getRecents().filter((r) => r !== term)].slice(0, 8);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(updated));
  } catch {}
};

const SearchModal = ({ isOpen, onClose, onSubmit, onScanClick }) => {
  const [value, setValue] = useState('');
  const [recents, setRecents] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setValue('');
      setRecents(getRecents());
      // Small delay so the animation is visible before focus opens keyboard
      const id = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (term) => {
    const t = (term ?? value).trim();
    if (!t) return;
    saveRecent(t);
    hapticLight();
    onSubmit(t);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit(value);
    if (e.key === 'Escape') onClose();
  };

  const clearRecents = () => {
    localStorage.removeItem(RECENTS_KEY);
    setRecents([]);
  };

  return (
    <div className="sm-overlay" role="dialog" aria-modal="true" aria-label="Pesquisar produtos">
      {/* Search bar row */}
      <div className="sm-bar">
        <div className="sm-input-wrap">
          <svg className="sm-input-icon" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
            <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            ref={inputRef}
            className={`sm-input${!value && !onScanClick ? ' sm-input--no-btn' : ''}`}
            type="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            placeholder="Nome do produto ou EAN…"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {value ? (
            <button className="sm-clear" onClick={() => { setValue(''); inputRef.current?.focus(); }} aria-label="Limpar">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" fill="currentColor" opacity="0.15"/>
                <path d="M9 9l6 6M15 9l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>
          ) : onScanClick ? (
            <button className="sm-scan-btn" onClick={() => { onClose(); onScanClick(); }} aria-label="Digitalizar código de barras">
              <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
                <path d="M3 5h3v2H5v2H3V5Zm18 0v4h-2V7h-1V5h3ZM3 15h2v2h1v2H3v-4Zm16 2v-2h2v4h-3v-2h1Z" fill="currentColor"/>
                <rect x="6" y="7" width="1.2" height="10" rx="0.4" fill="currentColor"/>
                <rect x="8.1" y="7" width="0.9" height="10" rx="0.4" fill="currentColor"/>
                <rect x="10" y="7" width="1.4" height="10" rx="0.4" fill="currentColor"/>
                <rect x="12.2" y="7" width="0.8" height="10" rx="0.4" fill="currentColor"/>
                <rect x="14" y="7" width="1.4" height="10" rx="0.4" fill="currentColor"/>
                <rect x="16.2" y="7" width="0.9" height="10" rx="0.4" fill="currentColor"/>
              </svg>
            </button>
          ) : null}
        </div>
        <button className="sm-cancel" onClick={onClose}>Cancelar</button>
      </div>

      {/* Recent searches */}
      {recents.length > 0 && !value && (
        <div className="sm-section">
          <div className="sm-section-header">
            <span className="sm-section-title">Recentes</span>
            <button className="sm-section-action" onClick={clearRecents}>Limpar</button>
          </div>
          <div className="sm-chips">
            {recents.map((r) => (
              <button key={r} className="sm-chip" onClick={() => handleSubmit(r)}>
                <svg viewBox="0 0 24 24" fill="none" width="13" height="13">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
                  <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
                {r}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Categories grid — shown when no query */}
      {!value && (
        <div className="sm-section">
          <div className="sm-section-header">
            <span className="sm-section-title">Categorias</span>
          </div>
          <div className="sm-cat-grid">
            {SEARCH_CATEGORIES.map(({ label, emoji }) => (
              <button key={label} className="sm-cat-tile" onClick={() => handleSubmit(label)}>
                <span className="sm-cat-emoji">{emoji}</span>
                <span className="sm-cat-label">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search action when user has typed something */}
      {value.trim() && (
        <button className="sm-search-action" onClick={() => handleSubmit(value)}>
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
            <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Pesquisar "<strong>{value.trim()}</strong>"
          <svg viewBox="0 0 24 24" fill="none" width="18" height="18" className="sm-search-arrow">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}
    </div>
  );
};

export default SearchModal;
